import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * `data-screen-label="Agents"` — the list every customer lands on once they have more than
 * one VoiceAgent (P11-01).
 *
 * WHY THE FILTER AND THE SEARCH ARE IN THE URL. Same reason History's are: "the paused ones"
 * is a link someone sends a colleague, and a filter held in component state is a screen that
 * cannot be shared. Both are typed search parameters on the route, validated to a union and
 * a string, so a hand-edited address produces a valid screen rather than a blank one.
 *
 * "ANSWERING NOW" (`isAnsweringNow`) counts an agent that is live AND has a number to be reached on
 * (review #107): a live agent with no number connected cannot answer anything. The count line above
 * the title left with the 2026-10-09 export.
 *
 * SEARCH IGNORES CASE AND ACCENTS (review #108). Greek is typed without the tonos as often as with
 * it, so "Παραγγελιες" finds "Παραγγελίες"; final sigma is folded too. Two agents with the same
 * name each show when they were created, so they can be told apart.
 *
 * AMENDED 2026-10-09 (design sync): no count line over the title; "Create new agent" beside it (the top bar's "New
 * agent" is gone); each row's third line opens with what the agent does — "Answers calls", or "Makes calls" for a
 * survey; the rows take the console's card finish.
 *
 * "CALLING" (plan-015 LST, G15): a live agent whose campaign is dialling now reads "Calling" where it would read
 * "Live" (`voiceAgentStatusOf`), from the list's `campaign_dialling`. The filter still keeps it under "Live": its
 * state is live, and a chip that hid a live agent from "Live" would hide the one that is busiest.
 *
 * MONEY IS WHAT BILLING CHARGES (review #109): "This month" is the seconds the free trial did not
 * cover, the same figure Billing and History show.
 *
 * WHAT IS DELIBERATELY NOT HERE, AND WHY.
 *
 *   · **No number where there is none.** `party_address` is nullable and the row says
 *     "no number yet" rather than showing a placeholder that reads like one. A VoiceAgent
 *     exists as a row before telephony binds an endpoint to it, and that is a state worth
 *     seeing rather than papering over.
 *   · **No "answering now" per row.** The list carries today's minutes and this month's
 *     spend, which the API supplies per VoiceAgent. How many conversations one agent is
 *     holding *right now* is the live board's figure and needs `/v1/conversations/live` per
 *     agent — N requests to render one column. The Live tab has it, one agent at a time,
 *     which is where it is worth the read.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Icon, Input, Segmented, Tag, type PhosphorIcon } from "@wetalk/ui";
import type { ReactNode } from "react";

import { VoiceAgentAudienceTag } from "./agent-audience.js";
import { GLYPH } from "../../shell/glyph.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { useGateway } from "../../api/provider.js";
import { isSurveyAgentTemplate } from "./[id]/agent-tabs.js";
import { voiceAgentListQuery } from "../../api/query.js";
import { formatMoney } from "../../money.js";
import { talkTimeText } from "../../talk-time.js";
import type { VoiceAgentState, VoiceAgentView } from "../../api/gateway.js";
import "./screen.css";
import "./list.css";

/** The design's four filter chips, plus Failed (review #38, #107), and the state each one keeps. */
export const VOICE_AGENT_FILTER = ["all", "live", "paused", "building", "failed"] as const;
export type VoiceAgentFilter = (typeof VOICE_AGENT_FILTER)[number];

export function isVoiceAgentFilter(value: string): value is VoiceAgentFilter {
  return (VOICE_AGENT_FILTER as readonly string[]).includes(value);
}

const FILTER_OPTION = [
  { value: "all", get label() { return portalText("operations", "all_6a720856"); } },
  { value: "live", get label() { return portalText("operations", "live_65c821a5"); } },
  { value: "paused", get label() { return portalText("operations", "paused_c7dfb6f1"); } },
  { value: "building", get label() { return portalText("operations", "building_9d914bfc"); } },
  { value: "failed", get label() { return portalText("operations", "failed_09fef5d8"); } },
] as const;

/** What a row's status chip says, and in which colour. */
export interface VoiceAgentStatusView {
  readonly label: string;
  readonly tone: "accent" | "accent-2" | "neutral";
}

/**
 * The prototype's `status` and `statusCls`: Live, Calling and Building are accent, a failed build is the danger
 * colour, everything else neutral.
 *
 * plan-015 LST (G15): "Calling" is a live agent with a campaign dialling now (`campaignDialling`, the list's
 * `campaign_dialling` — a campaign in the running state, never a scheduled or paused one). It takes the place of
 * "Live", as the design's survey row does; a paused agent stays "Paused" (its campaign is not reaching anybody),
 * and so does an agent WeTalk paused (`pausedBy: "operator"`, still `live` in the table). A row without the field
 * (a read that does not serve it) draws its own state.
 */
export function voiceAgentStatusOf(one: VoiceAgentView): VoiceAgentStatusView {
  if (one.state === "live" && one.pausedBy === null && one.campaignDialling === true) {
    return { label: portalText("operations", "calling_91f63fb7"), tone: "accent" };
  }
  if (one.state === "failed") return { label: stateLabel(one.state), tone: "accent-2" };
  return {
    label: stateLabel(one.state),
    tone: one.state === "live" || one.state === "building" ? "accent" : "neutral",
  };
}

function StatusTag({ voiceAgent }: { readonly voiceAgent: VoiceAgentView }): ReactNode {
  usePortalTranslation("operations");
  const status = voiceAgentStatusOf(voiceAgent);
  return <Tag tone={status.tone}>{status.label}</Tag>;
}

/** The word a customer reads, for a state the database names in its own vocabulary. */
function stateLabel(state: VoiceAgentState): string {
  switch (state) {
    case "live":
      return portalText("operations", "live_65c821a5");
    case "building":
      return portalText("operations", "building_9d914bfc");
    case "paused":
      return portalText("operations", "paused_c7dfb6f1");
    case "scheduled":
      return portalText("operations", "scheduled_1cd1bdad");
    case "failed":
      return portalText("operations", "build_failed_2f8e461f");
    default:
      return portalText("operations", "draft_23d33e22");
  }
}

function matchesFilter(one: VoiceAgentView, filter: VoiceAgentFilter): boolean {
  if (filter === "all") return true;
  return one.state === filter;
}

/**
 * Text as search compares it: accents and the tonos removed, case folded, Greek final sigma read
 * as sigma, whitespace collapsed (review #108).
 */
export function foldedText(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("el")
    .replace(/ς/g, "σ")
    .replace(/\s+/g, " ")
    .trim();
}

export function matchesSearch(one: VoiceAgentView, wanted: string): boolean {
  const needle = foldedText(wanted);
  if (needle === "") return true;
  const digits = wanted.replace(/\D/g, "");
  return (
    foldedText(one.displayName).includes(needle) ||
    foldedText(one.partyNumber ?? "").includes(needle) ||
    (digits.length >= 3 && (one.partyNumber ?? "").replace(/\D/g, "").includes(digits)) ||
    foldedText(one.agentTemplateName).includes(needle)
  );
}

/** A live agent answers only when something routes to it: a connected number (review #107). */
export function isAnsweringNow(one: VoiceAgentView): boolean {
  return one.state === "live" && one.partyNumber !== null;
}

/** The names that more than one agent carries, folded, so each copy can say which it is. */
export function duplicateNamesOf(all: readonly VoiceAgentView[]): ReadonlySet<string> {
  const seen = new Map<string, number>();
  for (const one of all) {
    const name = foldedText(one.displayName);
    seen.set(name, (seen.get(name) ?? 0) + 1);
  }
  return new Set([...seen].filter(([, count]) => count > 1).map(([name]) => name));
}

/** `created 24 Sep 14:05`, for telling two agents with one name apart. */
function createdText(createdAt: string | undefined): string | null {
  if (createdAt === undefined) return null;
  const at = new Date(createdAt);
  if (Number.isNaN(at.getTime())) return null;
  const day = new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { day: "numeric", month: "short" }).format(at);
  const clock = `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
  return portalText("operations", "created_value_value_bdb6148d", { v0: String(day), v1: String(clock) });
}

export interface VoiceAgentListScreenProps {
  readonly filter: VoiceAgentFilter;
  readonly search: string;
  readonly onFilter: (value: VoiceAgentFilter) => void;
  readonly onSearch: (value: string) => void;
}

export function VoiceAgentListScreen(props: VoiceAgentListScreenProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const all = useQuery(voiceAgentListQuery(gateway));
  // The page's one create button since the top bar's "New agent" left (design sync 2026-10-09), withheld with the
  // reason from a role that cannot create an agent.
  const createRefusal = useMemberCan().whyNot("voice_agent.write");

  if (all.data === undefined) {
    return <p className="c-note">{portalText("operations", "reading_your_agents_b9dbc2d1")}</p>;
  }

  const total = all.data.length;
  const duplicate = duplicateNamesOf(all.data);
  const shown = all.data.filter(
    (one) => matchesFilter(one, props.filter) && matchesSearch(one, props.search),
  );

  return (
    <section className="c-screen">
      <div className="c-agents-head">
        <div>
          <h1 className="c-screen-title">{portalText("operations", "agents_64acf7e2")}</h1>
        </div>
        {createRefusal === null ? (
          <Link to="/agents/new" className="btn btn-primary">
            <Icon glyph={GLYPH.plus} size="md" />
            {portalText("operations", "create_new_agent_b64126ff")}</Link>
        ) : (
          <button type="button" className="btn btn-primary" disabled title={createRefusal}>
            <Icon glyph={GLYPH.plus} size="md" />
            {portalText("operations", "create_new_agent_b64126ff")}</button>
        )}
      </div>

      {total === 0 ? (
        <div className="c-panel c-panel-dashed">
          <p className="c-empty-title">{portalText("operations", "nothing_is_answering_yet_dfd8ec36")}</p>
          <p className="c-empty-dek">
            {portalText("operations", "pick_a_template_hand_over_your_menu_or_your_questions_and_ac6f7914")}<Link to="/agents/new">{portalText("operations", "create_your_first_agent_e9f219b1")}</Link>.
          </p>
        </div>
      ) : (
        <>
          <div className="c-agents-controls">
            <Segmented
              name="voiceAgentFilter"
              legend={portalText("operations", "status_bae7d5be")}
              value={props.filter}
              options={FILTER_OPTION}
              onChange={props.onFilter}
            />
            <div className="c-agents-search">
              <span className="c-agents-search-glyph">
                <Icon glyph={GLYPH.magnifyingGlass} size="md" />
              </span>
              <Input
                type="search"
                placeholder={portalText("operations", "filter_by_name_or_number_e0b4cf44")}
                aria-label={portalText("operations", "filter_agents_by_name_or_number_cf8ad781")}
                value={props.search}
                onChange={(event) => {
                  props.onSearch(event.currentTarget.value);
                }}
              />
            </div>
          </div>

          {shown.length === 0 ? (
            <p className="c-agents-empty">
              {props.search.trim() === "" ? portalText("operations", "no_agents_with_that_status_4c187758") : portalText("operations", "no_agents_match_that_search_3b4591d3")}{" "}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  props.onFilter("all");
                  props.onSearch("");
                }}
              >
                {portalText("operations", "show_all_50a279de")}</button>
            </p>
          ) : (
            <div className="c-agents-list">
              {shown.map((one) => (
                <Link
                  key={one.voiceAgentId}
                  className="c-agents-row"
                  to="/agents/$voiceAgentId"
                  params={{ voiceAgentId: one.voiceAgentId }}
                  search={{
                    tab: "live",
                    range: "7d",
                    outcome: "all",
                    q: "",
                    conversation: "",
                    analyticsRange: "14d",
                    agentVersion: "",
                    concern: "gaps",
                  }}
                >
                  <span className="c-agents-row-mark">
                    <Icon glyph={glyphForAgentTemplate(one.agentTemplateCode)} size="2xl" />
                  </span>
                  <span className="c-agents-row-body">
                    <span className="c-agents-row-title">
                      <span className="c-agents-row-name">{one.displayName}</span>
                      <VoiceAgentAudienceTag agentTemplateCode={one.agentTemplateCode} voiceAgentId={one.voiceAgentId} />
                      <StatusTag voiceAgent={one} />
                      {duplicate.has(foldedText(one.displayName)) ? (
                        <span className="c-agents-row-twin">{createdText(one.createdAt) ?? one.liveAgentVersionLabel}</span>
                      ) : null}
                    </span>
                    <span className="c-agents-row-meta">
                      {one.agentTemplateName} ·{" "}
                      <span className="c-agents-row-number">
                        {one.partyNumber ?? portalText("operations", "no_number_connected_yet_fdf8636f")}
                      </span>
                    </span>
                    <span className="c-agents-row-sub">
                      {isSurveyAgentTemplate(one.agentTemplateCode) ? portalText("operations", "makes_calls_04772d40") : portalText("operations", "answers_calls_1260b2f0")} ·{" "}
                      {one.languageCode.length === 0
                        ? portalText("operations", "no_language_configured_bd2cfb47")
                        : one.languageCode.map((code) => code.toUpperCase()).join(" · ")}{" "}
                      · {one.liveAgentVersionLabel}
                    </span>
                  </span>
                  <span className="c-agents-row-figures">
                    <span>
                      <span className="c-agents-row-figure-value">{talkTimeText(one.talkSecondToday)}</span>
                      <span className="c-agents-row-figure-label">{portalText("operations", "min_today_8e4735b3")}</span>
                    </span>
                    <span title={portalText("operations", "what_billing_charges_this_month_your_free_minutes_are_used_fccb0a69")}>
                      <span className="c-agents-row-figure-value">
                        {formatMoney(one.spendThisMonth)}
                      </span>
                      <span className="c-agents-row-figure-label">{portalText("operations", "this_month_1b478533")}</span>
                    </span>
                  </span>
                  <span className="c-agents-row-open">
                    {portalText("operations", "deep_dive_f96539f6")}<Icon glyph={GLYPH.arrowRight} size="md" />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The row's glyph.
 *
 * `shell/glyph.ts`'s `glyphOf` resolves the catalogue's own icon STRING and raises on one the
 * console does not carry, which is right for the picker. The Agents list does not have that
 * string — `GET /v1/agents` carries the template's *code*, not its icon — so the mapping is by
 * code, with the headset for a template this build has not been taught. That is a fallback
 * for a GLYPH, not for a value: nothing about the row's meaning changes and no figure is
 * invented.
 */
function glyphForAgentTemplate(agentTemplateCode: string): PhosphorIcon {
  switch (agentTemplateCode) {
    case "delivery":
      return GLYPH.moped;
    case "survey":
      return GLYPH.clipboardText;
    case "booking":
      return GLYPH.calendarCheck;
    case "support":
      return GLYPH.lifebuoy;
    case "qualify":
      return GLYPH.funnel;
    case "reminder":
      return GLYPH.bellRinging;
    default:
      return GLYPH.headset;
  }
}
